import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, FlexibleConnectedPositionStrategyOrigin, ViewportRuler } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, effect, HostListener, inject, input, OnInit, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { EnpsControlComponent } from '@common/controls/enps-control/enps-control';
import { KeyName } from '@src/suite/common/utils/util';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'st-text-area-overlay',
    templateUrl: './st-text-area-overlay.html',
    styleUrls: ['./st-text-area-overlay.scss'],
    host: {
        class: 'st-text-area-overlay',
        tabindex: '0',
    },
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    hostDirectives: [CdkOverlayOrigin],
})
export class StTextAreaOverlayComponent extends EnpsControlComponent<string> implements OnInit {
    _viewportRuler = inject(ViewportRuler);
    panelClass = input<string>('');
    minHeight = input<string>('');
    textToHighlight = input<string>('');
    minRows = input<number, any>(1, {
        transform: val => coerceNumberProperty(val, 1),
    });
    maxRows = input<number, any>(3, {
        transform: val => coerceNumberProperty(val, 3),
    });

    disableNewLine = input(false, {
        transform: coerceBooleanProperty,
    });

    maxOverlayRows = input<number, any>(15, {
        transform: val => coerceNumberProperty(val, 15),
    });

    overlay = viewChild<CdkConnectedOverlay>('overlay');
    _triggerRect = signal<DOMRect>(null);
    isPanelOpen = signal(false);
    internalValue = signal<string>('');

    get origin(): CdkOverlayOrigin | FlexibleConnectedPositionStrategyOrigin {
        return this.elmRef;
    }

    displayValue = computed(() => {
        return this.internalValue() || this.placeholder();
    });

    get connectedPositions(): ConnectedPosition[] {
        return [
            {
                originX: 'start',
                originY: 'top',
                overlayX: 'start',
                overlayY: 'top',
            },
        ];
    }

    constructor() {
        super();
        effect(
            () => {
                this.internalValue.set(this.value());
            },
            { allowSignalWrites: true }
        );
    }

    ngOnInit() {
        // Check rect on resize
        this._viewportRuler
            .change()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                if (this.isPanelOpen()) {
                    this._triggerRect.set(this.elmRef.nativeElement.getBoundingClientRect());
                    this.changeDetectorRef.detectChanges();
                }
            });
    }

    @HostListener('click', ['$event'])
    @HostListener('keydown.space', ['$event'])
    onContainerClick(event?: KeyboardEvent) {
        event?.preventDefault();
        this._triggerRect.set(this.elmRef.nativeElement.getBoundingClientRect());
        this.isPanelOpen.set(true);
    }

    onNgModelChanged(value: string) {
        this.internalValue.set(value);
        this.changeDetectorRef.detectChanges();
        this._triggerRect.set(this.elmRef.nativeElement.getBoundingClientRect());
    }

    onValueChanged() {
        if (this.disableNewLine()) {
            this.internalValue.set(this.internalValue().replace(/\n|\r/gm, ' '));
        }
        this.changed.emit(this.internalValue());
    }

    panelAttached() {
        const textarea = this.overlay()?.overlayRef?.overlayElement?.getElementsByTagName('textarea')?.[0];

        if (textarea instanceof HTMLTextAreaElement) {
            textarea.focus();
        }
    }

    onFocusOut(event) {
        if (!this.elmRef.nativeElement.contains(event.relatedTarget as Element)) {
            this.elmRef.nativeElement.focus();
            this.isPanelOpen.set(false);
        }
    }

    onOverlayKeydown(event: KeyboardEvent) {
        if (event.key === KeyName.Escape) {
            this.elmRef.nativeElement.focus();
            this.internalValue.set(this.value());
            this.isPanelOpen.set(false);
        }
    }

    onTextAreaEnter(event: Event) {
        if (this.disableNewLine()) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
}
